import { BusinessType, BusinessUnitInterface } from '../interfaces'

/**
 * Fila de contrato que la regla necesita leer.
 *
 * La fuente única es `GET /Agreement/AgreementBU?AgreementID=` (ver
 * `useContractDestinationBUs`). El tipo es estructural y no el DTO concreto para
 * que la regla no dependa de una tabla en particular: `AccountSiteBU`, que se usó
 * antes, tiene exactamente estos campos y sigue encajando.
 */
export interface ContractBURow {
  buCode: string
  isSource: boolean
  isLocked: boolean
  fromDate?: string | null
  toDate?: string | null
  statusID?: number | null
}

/**
 * Resuelve las tiendas de DESTINO que un contrato corporativo puede usar, a partir
 * de las filas de `AgreementBU`: `isSource=false` marca una tienda de destino.
 *
 * Espeja exactamente la regla de ApiCore en
 * TEALCA.ApiCore/Controllers/ShipmentController.cs:396-419
 *
 *   allowAnyBU = Model == null || !Model.Any() || Model.All(b => b.IsSource)
 *
 * ...para que el desplegable y el servidor nunca puedan estar en desacuerdo.
 *
 *   rows === undefined ──▶ aún no se consultó ──▶ catálogo por defecto, restricted: false
 *           │
 *           ▼
 *   rows.some(r => !r.isSource) ?          ◀── el disparador corre sobre las filas CRUDAS,
 *           │                                  igual que All(b => b.IsSource) en ApiCore.
 *           ├── NO  ──▶ catálogo por defecto, restricted: false
 *           │           (descarta KK/NN, descarta isLocked, deja EXTERNAL|INTERNAL)
 *           │
 *           └── SÍ  ──▶ restricted: true
 *                       options = filas usables → Map por buCode normalizado
 *                                 contra el catálogo COMPLETO
 *                                 (ignora los filtros de buTypeID y isLocked)
 *                       sin match en catálogo ──▶ console.warn + descartar
 *                       NUNCA se fabrica una tienda: las coordenadas alimentan
 *                       getTier() y una tienda inventada tarifica mal.
 *
 * Divergencia intencional: ApiCore sólo valida pertenencia e IsLocked, nunca
 * statusID ni la ventana de fechas. Aquí somos MÁS estrictos, que es el lado
 * seguro (nunca produce un 400). Registrado en TODOS.md.
 */

export const CONTRACT_BU_ACTIVE_STATUS = 1

/** Códigos que nunca son destinos válidos, heredado del filtro original. */
const EXCLUDED_BU_CODES = ['KK', 'NN']

/**
 * ¿Es una tienda Junior?
 *
 * Una Junior SÍ puede ser la unidad de negocio destino de una entrega a DOMICILIO —ahí la
 * deriva el sistema por cobertura/ciudad, el operador no la elige— pero NUNCA puede
 * ofrecerse como retiro en Tienda.
 *
 * Se mira el `buTypeID` primero porque el id es el criterio confiable; el catálogo .NET lo
 * trae y ahí el filtro 10/20 de `defaultCatalogOptions` ya las descartaba. El fallback por
 * nombre existe para el catálogo de Django (`GET /api/store`), que es el de carga masiva y
 * solo devuelve `store_type_name` — texto de BD, sin id. Cuando el serializer exponga
 * `store_type_id` esta función resuelve sola por la primera rama.
 */
export const isJuniorStore = (businessUnit: BusinessUnitInterface): boolean =>
  businessUnit.buTypeID === BusinessType.JUNIOR ||
  (businessUnit.storeTypeName ?? '').trim().toLowerCase() === 'junior'

export interface DestinationBusinessUnits {
  /** Tiendas seleccionables. Sin ordenar: cada pantalla aplica su propio sort. */
  options: BusinessUnitInterface[]
  /** true cuando el contrato trae al menos una fila de destino (isSource=false). */
  restricted: boolean
}

/**
 * ApiCore serializa en camelCase (`AddControllers()` por defecto), pero el DTO
 * hereda de BaseEntity y redeclara `public new string BUCode`, así que leemos
 * ambas formas por si la serialización cambia bajo un upgrade.
 */
interface RawContractBU {
  BUCode?: string
  IsSource?: boolean
  IsLocked?: boolean
  StatusID?: number | null
}

const readBuCode = (row: ContractBURow): string =>
  (row as RawContractBU).BUCode ?? row.buCode ?? ''

const readIsSource = (row: ContractBURow): boolean =>
  Boolean((row as RawContractBU).IsSource ?? row.isSource)

const readIsLocked = (row: ContractBURow): boolean =>
  Boolean((row as RawContractBU).IsLocked ?? row.isLocked)

const readStatusID = (row: ContractBURow): number | null | undefined =>
  (row as RawContractBU).StatusID ?? row.statusID

const normalizeCode = (code?: string | null): string =>
  (code ?? '').trim().toLowerCase()

/**
 * Clave de deduplicación tolerante al catálogo de origen.
 *
 * El catálogo de .NET trae `buid` numérico y único, y por ahí se dedupe siempre.
 * El de Django (`GET /api/store`, el que usa carga masiva) NO trae buid: el
 * mapper deja `buid: 0` en TODAS las tiendas (mappers.tsx:388), así que deduplicar
 * por buid colapsaría el catálogo entero en una sola tienda. Ahí la clave es el
 * buCode, que sí es único.
 */
const dedupeKey = (businessUnit: BusinessUnitInterface): string =>
  businessUnit.buid > 0
    ? `id:${businessUnit.buid}`
    : `code:${normalizeCode(businessUnit.buCode)}`

/**
 * Compara por día calendario, no por instante. `fromDate`/`toDate` vienen de un
 * DateTime de .NET sin zona horaria; parsear con `new Date()` movería el límite
 * un día para un cliente en Venezuela, así que leemos el YYYY-MM-DD literal.
 */
const calendarDayFromText = (value?: string | null): number | null => {
  if (!value) return null
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (iso) {
    return Number(iso[1]) * 10000 + Number(iso[2]) * 100 + Number(iso[3])
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return calendarDayFromDate(parsed)
}

const calendarDayFromDate = (value: Date): number =>
  value.getFullYear() * 10000 + (value.getMonth() + 1) * 100 + value.getDate()

/**
 * ¿Esta fila del contrato habilita hoy una tienda de destino?
 *
 * `now` se recibe por parámetro para que las pruebas no dependan del reloj.
 */
export const isContractRowUsable = (
  row: ContractBURow,
  now: Date
): boolean => {
  if (readIsSource(row)) return false
  if (readIsLocked(row)) return false

  const status = readStatusID(row)
  if (status != null && status !== CONTRACT_BU_ACTIVE_STATUS) return false

  const today = calendarDayFromDate(now)

  const from = calendarDayFromText(row.fromDate)
  if (from != null && today < from) return false

  // toDate nulo o ausente = vigencia abierta.
  const to = calendarDayFromText(row.toDate)
  if (to != null && today > to) return false

  return true
}

/** El filtro histórico del catálogo, idéntico al que vivía duplicado en 4 pantallas. */
const defaultCatalogOptions = (
  catalog: BusinessUnitInterface[]
): BusinessUnitInterface[] => {
  const seen = new Set<string>()
  const options: BusinessUnitInterface[] = []

  catalog.forEach((businessUnit) => {
    if (EXCLUDED_BU_CODES.includes(businessUnit.buCode)) return
    if (businessUnit.isLocked) return
    // Junior: válida como destino a domicilio, nunca elegible como retiro en Tienda.
    // Va aparte del filtro por tipo de abajo porque ese solo corre cuando el catálogo
    // trae buTypeID, y el de Django (carga masiva) no lo trae.
    if (isJuniorStore(businessUnit)) return
    // El filtro por tipo solo aplica cuando el catálogo LO TRAE. El de Django no
    // tiene buTypeID (queda undefined para todas), así que exigirlo ahí dejaría
    // la lista vacía; ese catálogo ya viene filtrado a tiendas activas de origen.
    if (
      businessUnit.buTypeID != null &&
      businessUnit.buTypeID !== BusinessType.EXTERNAL &&
      businessUnit.buTypeID !== BusinessType.INTERNAL
    ) {
      return
    }
    const key = dedupeKey(businessUnit)
    if (seen.has(key)) return

    seen.add(key)
    options.push(businessUnit)
  })

  return options
}

export const resolveDestinationBusinessUnits = (
  catalog: BusinessUnitInterface[],
  rows: ContractBURow[] | undefined,
  now: Date
): DestinationBusinessUnits => {
  // Todavía no se consultó el contrato: comportamiento de hoy.
  if (rows === undefined) {
    return { options: defaultCatalogOptions(catalog), restricted: false }
  }

  // El disparador corre sobre las filas CRUDAS. Vigencia y bloqueo deciden QUÉ
  // tiendas se ofrecen, nunca SI el contrato restringe. Un contrato cuya única
  // fila de destino está bloqueada queda restringido con cero destinos válidos,
  // y eso es lo correcto: es exactamente lo que ApiCore va a rechazar.
  const hasDestinationRow = rows.some((row) => !readIsSource(row))
  if (!hasDestinationRow) {
    return { options: defaultCatalogOptions(catalog), restricted: false }
  }

  // Índice sobre el catálogo COMPLETO: las tiendas del contrato deben poder
  // saltarse los filtros de buTypeID (las virtuales son 140/160) y de isLocked.
  const catalogByCode = new Map<string, BusinessUnitInterface>()
  catalog.forEach((businessUnit) => {
    const key = normalizeCode(businessUnit.buCode)
    if (key && !catalogByCode.has(key)) catalogByCode.set(key, businessUnit)
  })

  const seen = new Set<string>()
  const options: BusinessUnitInterface[] = []

  rows.forEach((row) => {
    if (!isContractRowUsable(row, now)) return

    const buCode = readBuCode(row)

    // KK/NN son códigos centinela que TODAS las pantallas descartan (y también
    // Innovus). El contrato puede listarlos igual (el AccountSite 1772 lista NN)
    // y ApiCore los aceptaría, pero ofrecerlos rompería la convención de la
    // aplicación. Mismo criterio deliberado que con statusID y las fechas:
    // somos más estrictos que el servidor, que es el lado seguro.
    if (EXCLUDED_BU_CODES.includes(buCode.trim())) return

    const businessUnit = catalogByCode.get(normalizeCode(buCode))

    if (!businessUnit) {
      console.warn(
        `[destinos-contrato] buCode del contrato sin match en el catálogo: ${buCode}`
      )
      return
    }

    const key = dedupeKey(businessUnit)
    if (seen.has(key)) return

    seen.add(key)
    options.push(businessUnit)
  })

  return { options, restricted: true }
}

/**
 * Completa un catálogo con las tiendas de DESTINO que el contrato referencia y que ese
 * catálogo no trae.
 *
 * Existe por una asimetría real entre las dos fuentes de tiendas de la aplicación:
 *
 *   .NET    `GET /BusinessUnit/BusinessUnitV2` → trae TODAS, incluidas las virtuales
 *                                                (`buTypeID` 140/160). 180 en dev.
 *   Django  `GET /api/store`                   → NO trae las virtuales. 136 en dev.
 *
 * Carga masiva resuelve contra el catálogo de Django (`bulkCatalogs`). Un contrato
 * corporativo armado sobre tiendas virtuales —que es justo el caso de retiro por
 * plataforma y de devoluciones— quedaba sin destinos: `resolveDestinationBusinessUnits`
 * no encontraba sus buCode y los descartaba uno por uno con un `console.warn`.
 * El mismo contrato en Crear guía, que usa el catálogo de .NET, sí ofrecía sus tiendas.
 *
 * El síntoma para el operador no era un mensaje de "tienda no permitida" sino algo peor:
 * el código de la tienda no llegaba al campo, la celda quedaba vacía y la fila se marcaba
 * con "Requerido para retiro por oficina", sin ninguna pista de que el problema fuera el
 * catálogo.
 *
 * Se agregan ÚNICAMENTE las tiendas de destino del contrato que faltan. El catálogo por
 * defecto (contrato que no restringe) queda idéntico, así que esta función no puede
 * ensanchar la lista de un cliente sin contrato corporativo.
 *
 * Devuelve el MISMO array cuando no hay nada que agregar, para no romper la identidad
 * referencial de la que dependen los `useMemo` que la consumen.
 */
export const augmentCatalogWithContractStores = (
  catalog: BusinessUnitInterface[],
  fallbackCatalog: BusinessUnitInterface[] | undefined,
  rows: ContractBURow[] | undefined
): BusinessUnitInterface[] => {
  if (!rows?.length || !fallbackCatalog?.length) return catalog

  const present = new Set(catalog.map((bu) => normalizeCode(bu.buCode)))

  const missing = new Set<string>()
  rows.forEach((row) => {
    if (readIsSource(row)) return
    const code = normalizeCode(readBuCode(row))
    if (code && !present.has(code)) missing.add(code)
  })
  if (missing.size === 0) return catalog

  const extras: BusinessUnitInterface[] = []
  const added = new Set<string>()
  fallbackCatalog.forEach((businessUnit) => {
    const code = normalizeCode(businessUnit.buCode)
    if (!missing.has(code) || added.has(code)) return
    added.add(code)
    extras.push(businessUnit)
  })

  return extras.length ? [...catalog, ...extras] : catalog
}
